import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenantBrand } from "@/context/TenantBrandContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SIDE_IMG = "https://images.unsplash.com/photo-1551970353-3960cb854a2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxtdXNpYyUyMHNjaG9vbCUyMHBpYW5vJTIwc3R1ZGVudHxlbnwwfHx8fDE3ODcxMDczMTl8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const { brand } = useTenantBrand();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password, brand?.id);
      navigate(u.role === "super_admin" ? "/superadmin" : "/dashboard");
    } catch (err) {
      setError(apiError(err, "Gagal masuk. Periksa kembali email dan kata sandi."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#040D1D] font-['Plus_Jakarta_Sans',sans-serif]">
      <div className="relative hidden w-1/2 lg:block">
        <img src={SIDE_IMG} alt="Siswa bermain piano" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B2A5B]/85 via-[#071731]/80 to-[#040D1D]/95" />
        <div className="absolute bottom-0 left-0 p-10 text-white">
          <p className="font-['Manrope',sans-serif] text-3xl font-extrabold leading-snug">
            Kelola sekolah musik Anda<br />
            dengan{" "}
            <span className="bg-gradient-to-r from-blue-400 to-[#00D1A8] bg-clip-text text-transparent">
              teknologi Nexora
            </span>
            .
          </p>
          <p className="mt-3 max-w-md text-sm text-slate-300">
            Database siswa, jadwal les, pembayaran SPP, hingga laporan keuangan — semua dalam satu tempat.
          </p>
        </div>
      </div>

      <div className="relative flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="pointer-events-none absolute -top-24 right-0 h-72 w-72 rounded-full bg-[#2563EB]/15 blur-3xl" />
        <div className="relative w-full max-w-md">
          <Link to="/" className="mb-10 flex items-center gap-3" data-testid="login-brand">
            <img
              src={brand?.logo_url || "/nexora-logo.jpg"}
              alt={brand?.name || "Nexora"}
              className="h-12 w-12 rounded-xl object-cover shadow-[0_0_18px_rgba(0,209,168,0.25)]"
              data-testid="login-logo"
            />
            <div className="leading-tight">
              <p className="font-['Manrope',sans-serif] text-lg font-extrabold tracking-tight text-white" data-testid="login-brand-name">
                {brand?.name || "Aplikasi Sekolah Musik"}
              </p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00D1A8]">
                {brand ? "Powered by Nexora" : "by Nexora"}
              </p>
            </div>
          </Link>

          <h1 className="font-['Manrope',sans-serif] text-3xl font-extrabold tracking-tight text-white">Masuk Admin</h1>
          <p className="mt-2 text-sm text-slate-400">
            {brand ? `Silakan masuk ke sistem ${brand.name}.` : "Silakan masuk untuk mengelola data sekolah musik."}
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                placeholder="admin@sekolah.id"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-[#00D1A8]/50"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Kata Sandi</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-[#00D1A8]/50"
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_20px_rgba(0,209,168,0.3)] transition-opacity duration-200 hover:opacity-95"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Masuk
            </Button>
          </form>
          {!brand && (
            <p className="mt-6 text-center text-sm text-slate-400">
              Belum punya akun?{" "}
              <Link to="/daftar" className="font-semibold text-[#00D1A8] transition-colors hover:text-teal-300" data-testid="go-register-link">
                Daftarkan sekolah Anda
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
